import type { TooltipProps as MuiTooltipProps } from '@material-ui/core/Tooltip'
import { default as MuiTooltip } from '@material-ui/core/Tooltip'
import type { Theme } from '@material-ui/core/styles'
import { Badge, ClickAwayListener, makeStyles } from '@material-ui/core'
import React, { useEffect, useRef, useState } from 'react'
import HelpIcon from '@material-ui/icons/Help'
import type { StyleClasses } from '../../styles'
import { usePopperPositionFix } from '../../hooks'
import Typography from '../Typography'

const useStyles = makeStyles<Theme, TooltipProps>(
  (theme) => ({
    root: {},
    icon: {
      width: theme.typography.pxToRem(15),
      marginLeft: theme.typography.pxToRem(15),
      color: theme.paintscout.palette.primary
    },
    iconOnlyIcon: {
      width: theme.typography.pxToRem(15),
      color: theme.paintscout.palette.primary,
      display: 'block'
    },
    iconOnlyTapTarget: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: theme.spacing(1),
      margin: -theme.spacing(1),
      cursor: 'pointer',
      touchAction: 'manipulation',
      WebkitTapHighlightColor: 'transparent'
    },
    iconWrapper: {
      display: 'inline-flex',
      alignItems: 'center'
    },
    popper: {
      opacity: 1
    },
    htmlTooltip: (props) => ({
      backgroundColor: theme.paintscout.palette.brand.Neutral.P35,
      borderRadius: theme.borderRadius.md,
      opacity: 1,
      color: theme.palette.common.white,
      maxWidth: 350,
      minWidth: props.mapTooltip ? 350 : undefined,
      padding: 10,
      '& b': {
        fontWeight: theme.typography.fontWeightMedium
      },
      '& em': {
        fontStyle: 'italic'
      },
      '& a': {
        color: theme.palette.common.white
      }
    }),
    badgeRoot: {},
    badge: (props) => ({
      top: props?.badgeTop || '50%',
      right: props?.badgeRight || -5,
      marginTop: -1
    })
  }),
  { name: 'Tooltip' }
)

export interface TooltipProps extends Omit<MuiTooltipProps, 'title' | 'content' | 'children'> {
  children?: React.ReactElement<any, any>
  classes?: StyleClasses<typeof useStyles>
  title?: string
  content?: string | React.ReactNode
  component?: React.ReactNode
  timeout?: number
  autoOpen?: boolean
  icon?: React.ReactNode
  hideIcon?: boolean
  interactive?: boolean
  badgeTop?: string
  badgeRight?: string
  disabled?: boolean
  disableWrapper?: boolean
  stopIconClickPropagation?: boolean
  mapTooltip?: boolean
  iconOnly?: boolean
}

function Tooltip(props: TooltipProps) {
  const classes = useStyles(props)
  const {
    children,
    component,
    placement,
    title,
    content,
    autoOpen,
    interactive,
    enterDelay,
    enterNextDelay,
    disableWrapper,
    open,
    onOpen,
    onClose,
    PopperProps
  } = props
  const [openTooltip, setOpenTooltip] = useState<boolean>(false)
  const disabled = props.disabled || (!content && !title)
  const hideIcon = props.hideIcon || (!content && !title)

  // Avoid the Safari/iPad "blue flicker" where the popper paints at viewport (0,0) before it
  // positions. See usePopperPositionFix. The uncontrolled path triggers via `onOpen` below;
  // the controlled mapTooltip path has no onOpen, so recompute via an effect on `open`.
  const { popperRef, scheduleUpdate } = usePopperPositionFix()

  useEffect(() => {
    if (props.mapTooltip && open) scheduleUpdate()
  }, [props.mapTooltip, open, scheduleUpdate])

  useEffect(() => {
    if (autoOpen) {
      setTimeout(
        () => {
          setOpenTooltip(true)
          setTimeout(() => {
            setOpenTooltip(false)
          }, 5000)
        },
        props.timeout ? props.timeout : 5000
      )
    }
  }, [])

  const handleMouseEnter = () => {
    setOpenTooltip(true)
  }

  const handleMouseOut = () => {
    setOpenTooltip(false)
  }

  const tooltipContent = (
    <>
      {title && (
        <Typography variant={'h5'} color={'secondary'}>
          {title}
        </Typography>
      )}
      {content && (
        <Typography variant={'subtitle1'} color={'secondary'}>
          {content}
        </Typography>
      )}
    </>
  )

  const handleStopPropagationMouse = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation()
  }
  const handleStopPropagationTouch = (event: React.TouchEvent<HTMLElement>) => {
    event.stopPropagation()
  }
  const badgeMouseHandlers = props.stopIconClickPropagation
    ? {
        onClick: handleStopPropagationMouse,
        onMouseDown: handleStopPropagationMouse,
        onMouseUp: handleStopPropagationMouse
      }
    : {}
  const badgeTouchHandlers = props.stopIconClickPropagation
    ? {
        onTouchStart: handleStopPropagationTouch,
        onTouchEnd: handleStopPropagationTouch
      }
    : {}
  const iconContent = props.icon ? props.icon : hideIcon ? null : <HelpIcon className={classes.icon} />
  const icon =
    props.stopIconClickPropagation && iconContent ? (
      <span
        className={classes.iconWrapper}
        onClick={handleStopPropagationMouse}
        onMouseDown={handleStopPropagationMouse}
        onMouseUp={handleStopPropagationMouse}
        onTouchStart={handleStopPropagationTouch}
        onTouchEnd={handleStopPropagationTouch}
      >
        {iconContent}
      </span>
    ) : (
      iconContent
    )

  // DialogStackRenderer locks background scroll with `position: fixed; top: -${scrollY}px` on
  // body. The popper portals to body, so body becomes the containing block for the absolutely
  // positioned popper and it ends up `scrollY`px too high. `positionFixed: true` switches the
  // popper to position:fixed (anchored to the viewport), bypassing the body offset.
  const sharedTooltipProps = {
    title: tooltipContent,
    placement,
    classes: { tooltip: classes.htmlTooltip, popper: classes.popper },
    interactive,
    enterDelay,
    enterNextDelay,
    enterTouchDelay: 0,
    leaveTouchDelay: 3000,
    disableFocusListener: disabled || props.disableFocusListener,
    disableHoverListener: disabled || props.disableHoverListener,
    disableTouchListener: disabled || props.disableTouchListener,
    onOpen: (event: React.ChangeEvent<{}>) => {
      scheduleUpdate()
      onOpen?.(event)
    },
    onClose,
    ...(open !== undefined && { open }),
    PopperProps: { popperRef, popperOptions: { positionFixed: true } }
  } as const

  return (
    <>
      {autoOpen ? (
        <div onMouseEnter={handleMouseEnter.bind(this)} onMouseLeave={interactive ? null : handleMouseOut.bind(this)}>
          <MuiTooltip
            title={tooltipContent}
            open={openTooltip}
            placement={placement}
            classes={{ tooltip: classes.htmlTooltip, popper: classes.popper }}
            PopperProps={{ popperOptions: { positionFixed: true } }}
          >
            {component && !children ? (
              <Badge
                badgeContent={icon}
                onClick={() => {
                  setOpenTooltip((toolTipValue) => !toolTipValue)
                }}
                classes={{ root: classes.badgeRoot, badge: classes.badge }}
                {...badgeMouseHandlers}
                {...badgeTouchHandlers}
              >
                {component}
              </Badge>
            ) : (
              <Badge
                badgeContent={icon}
                classes={{ root: classes.badgeRoot, badge: classes.badge }}
                {...badgeMouseHandlers}
                {...badgeTouchHandlers}
              >
                {children}
              </Badge>
            )}
          </MuiTooltip>
        </div>
      ) : props.iconOnly ? (
        <Badge
          badgeContent={
            <IconOnlyTooltip
              sharedTooltipProps={sharedTooltipProps}
              icon={props.icon ? props.icon : hideIcon ? null : <HelpIcon className={classes.iconOnlyIcon} />}
              tapTargetClassName={classes.iconOnlyTapTarget}
            />
          }
          classes={{ root: classes.badgeRoot, badge: classes.badge }}
          {...badgeMouseHandlers}
          {...badgeTouchHandlers}
        >
          <div>{children}</div>
        </Badge>
      ) : (
        <MuiTooltip
          {...sharedTooltipProps}
          {...(props.mapTooltip
            ? {
                open,
                onOpen,
                onClose,
                PopperProps: {
                  ...PopperProps,
                  popperRef,
                  popperOptions: { positionFixed: true, ...PopperProps?.popperOptions }
                }
              }
            : {})}
        >
          {component && !children ? (
            <>{component}</>
          ) : disableWrapper ? (
            children
          ) : (
            <Badge
              badgeContent={icon}
              classes={{ root: classes.badgeRoot, badge: classes.badge }}
              {...badgeMouseHandlers}
              {...badgeTouchHandlers}
            >
              <div>{children}</div>
            </Badge>
          )}
        </MuiTooltip>
      )}
    </>
  )
}

interface IconOnlyTooltipProps {
  sharedTooltipProps: Omit<MuiTooltipProps, 'children'>
  icon: React.ReactNode
  tapTargetClassName: string
}

function IconOnlyTooltip({ sharedTooltipProps, icon, tapTargetClassName }: IconOnlyTooltipProps) {
  const [open, setOpen] = useState(false)
  const isTouchRef = useRef(false)

  // Badge transform + inline-flex anchor means the first paint lands far from the icon on
  // Safari/iPad; recompute one frame after opening so it snaps to the correct coords.
  const { popperRef, scheduleUpdate } = usePopperPositionFix()

  useEffect(() => {
    if (open) scheduleUpdate()
  }, [open, scheduleUpdate])

  const handleClick = (event: React.MouseEvent<HTMLSpanElement>) => {
    event.stopPropagation()
    setOpen((prev) => !prev)
  }

  const handleTouchStart = () => {
    isTouchRef.current = true
  }

  const handleMouseEnter = () => {
    if (!isTouchRef.current) setOpen(true)
  }

  const handleMouseLeave = () => {
    if (!isTouchRef.current) setOpen(false)
  }

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <MuiTooltip
        {...sharedTooltipProps}
        open={open}
        onClose={() => setOpen(false)}
        disableFocusListener
        disableHoverListener
        disableTouchListener
        PopperProps={{ popperRef, popperOptions: { positionFixed: true } }}
      >
        <span
          className={tapTargetClassName}
          role="button"
          tabIndex={0}
          onClick={handleClick}
          onTouchStart={handleTouchStart}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {icon}
        </span>
      </MuiTooltip>
    </ClickAwayListener>
  )
}

export default Tooltip
